import { constants, dom } from '../../utils';
const isEqual = require('lodash/isEqual');

const CONTENT_EDITABLE_TYPES = ['label', 'link']

export default {
    namespaced: true,
    state: {

    },
    getters: {
        activeCount(state, getters, rootState, rootGetters) {
            return rootGetters['form/activeFeatures'].length;
        },
        displayVal: (state, getters, rootState, rootGetters) => key => {
            if (!['width', 'height', 'x', 'y'].includes(key)) {
                console.warn('Invalid key for displayVal');
                return '';
            }

            if (rootGetters['user/settings'].formUseImperial) {
                return Math.round((parseInt(getters.val(key)) / constants.FORM_PPI) * 100) / 100;
            } else {
                return Math.round((parseInt(getters.val(key)) / constants.FORM_PPMM) * 10) / 10;
            }
        },
        isSet: () => prop => {
            return prop !== undefined && prop !== null;
        },
        isVisible: (state, getters, rootState, rootGetters) => item => {
            if (!getters.activeCount) return false;
            let activeFeatures = rootGetters['form/activeFeatures'];
            switch (item) {
                case 'alignment':
                    return getters.activeCount > 1;
                case 'calculate':
                    return getters.activeCount === 1 && getters.isSet(activeFeatures[0].calculate);
                case 'checkbox':
                    return activeFeatures.every((f) => f.availableConfig.includes('checkbox'))
                case 'checkboxGroup':
                    return !!rootGetters['form/activeFeatureGroup'] && getters.isVisible('checkbox');
                case 'currency':
                    return activeFeatures.every((f) => getters.isSet(f.currency));
                case 'dataElementLinks':
                    return rootGetters['form/form'](0).data.dataElements.length && activeFeatures.every((f) => f.dataElementLinkable);
                case 'dateFormat':
                    return activeFeatures.every((f) => getters.isSet(f.dateFormat));
                case 'deleteFeature':
                    return true;
                case 'description':
                    return getters.activeCount === 1 && getters.isSet(activeFeatures[0].description);
                case 'fieldOptions':
                    return activeFeatures.every((f) => f.availableConfig.includes('fieldOptions'));
                case 'fontOptions':
                    return getters.isVisible('fontSizeAndFamily') || getters.isVisible('textFormat');
                case 'fontSizeAndFamily':
                    return activeFeatures.every((f) => f.availableConfig.includes('fontSizeAndFamily'));
                case 'lock':
                    return activeFeatures.every((f) => getters.isSet(f.lock));
                case 'officeUse':
                    return activeFeatures.every((f) => getters.isSet(f.officeUse));
                case 'placeholder':
                    return activeFeatures.every((f) => getters.isSet(f.placeholder));
                case 'position':
                    return true;
                case 'printOutline':
                    return activeFeatures.every((f) => getters.isSet(f.printOutline));
                case 'readOnly':
                    return activeFeatures.every((f) => getters.isSet(f.readOnly));
                case 'required':
                    return (
                        getters.activeCount &&
                        activeFeatures.every((f) => {
                            return getters.isSet(f.required) && (f.type !== 'checkbox' || !f.groupId);
                        })
                    );
                case 'shapeOptions':
                    return activeFeatures.every((f) => f.availableConfig.includes('shapeFormat'));
                case 'signature':
                    return activeFeatures.every((f) => f.type === 'signature');
                case 'tabOrder':
                    return getters.activeCount === 1 && activeFeatures[0].focusable && !activeFeatures[0].readOnly;
                case 'tag':
                    return activeFeatures.every((f) => f.availableConfig.includes('tag'));
                case 'text':
                    return activeFeatures.every((f) => getters.isSet(f.text));
                case 'textAlign':
                    return activeFeatures.every((f) => getters.isSet(f.textAlign));
                case 'textFormat':
                    return activeFeatures.every((f) => f.availableConfig.includes('textFormat'));
                case 'timeFormat':
                    return activeFeatures.every((f) => getters.isSet(f.timeFormat));
                case 'url':
                    return activeFeatures.every((f) => getters.isSet(f.url));
                case 'validation':
                    return activeFeatures.every((f) => getters.isSet(f.validationRegex));
                case 'value':
                    return activeFeatures.every((f) => getters.isSet(f.value));
                default:
                    return false
            }
        },
        noSharedGroup(state, getters, rootState, rootGetters) {
            let activeFeatures = rootGetters['form/activeFeatures'];
            if (activeFeatures.length === 1) return true;
            let groupIds = [];

            for (let feature of activeFeatures) {
                if (!feature.groupId) continue;
                if (groupIds.includes(feature.groupId)) return false;
                groupIds.push(feature.groupId);
            }
            return true;
        },
        val: (state, getters, rootState, rootGetters) => key => {
            // returns object of merged feature values for all active features
            // a value is set to null if active features have different values
            let activeFeatures = rootGetters['form/activeFeatures'];
            if (!activeFeatures.length) return '';

            let values = {
                ...activeFeatures[0],
                ...rootGetters['form/featurePositions']([activeFeatures[0]])[0],
            };

            // Handle displaying html in a text input
            if (values.text && CONTENT_EDITABLE_TYPES.includes(values.type)) {
                values.text = dom.convertHtmlToText(values.text).replace(/\n/g, ' ');
            }

            activeFeatures.slice(1).forEach((feature) => {
                let tempFeature = {
                    ...feature,
                    ...rootGetters['form/featurePositions']([feature])[0],
                };

                // Handle displaying html in a text input
                if (tempFeature.text && CONTENT_EDITABLE_TYPES.includes(tempFeature.type)) {
                    tempFeature.text = dom.convertHtmlToText(tempFeature.text).replace(/\n/g, ' ');
                }

                for (let key in values) {
                    if (tempFeature[key] === undefined) {
                        delete values[key];
                    } else if (!isEqual(values[key], tempFeature[key])) {
                        values[key] = null;
                    }
                }

                // this needs to be cleared seperately, because we keep the original fontSize
                // value when toggling autoFontSize
                if (tempFeature.autoFontSize && values.fontSize !== undefined) {
                    values.fontSize = null;
                }
            });

            if (values.required === false) values.required = ['never'];
            if (values.required === true) values.required = ['before_submit'];
            if (values.required === null) values.required = [];

            if (values.autoFontSize) {
                values.fontSize = 'Auto';
            }

            return values[key];
        },
        sectionWidths(state, getters) {
            let fieldOptionsWidth = 0;
            if (getters.isVisible('text')) fieldOptionsWidth += 210;
            if (getters.isVisible('lock') || getters.isVisible('required')) fieldOptionsWidth += 205;

            // add space for visible checkboxes
            let numFieldOptionsCheckboxes = ['officeUse', 'readOnly', 'checkbox', 'signature', 'printOutline'].reduce((acc, name) => getters.isVisible(name) ? acc + 1 : acc, 0);
            fieldOptionsWidth += Math.ceil(numFieldOptionsCheckboxes / 2) * 130;

            // add space for visible dropdown icons (duplicate values intended, to mimic DOM output)
            let numFieldOptionsIcons = ['calculate', 'checkbox', 'checkbox', 'dataElementLinks', 'description', 'signature', 'signature', 'tag', 'validation', 'value'].reduce((acc, name) => getters.isVisible(name) ? acc + 1 : acc, 0);


            // stacked in pairs, give 40px per pair
            fieldOptionsWidth += Math.ceil(numFieldOptionsIcons / 2) * 40;

            return {
                fieldOptions: fieldOptionsWidth,
                fontOptions: 190,
                shapeOptions: 90,
                position: 285, // varies by ~40px
                checkboxGroup: 245,
            };
        },
        sectionFits: (state, getters, rootState, rootGetters) => sectionName => {
            let rightSideWidth = 225;
            let w = rootGetters['page/width'] - rootGetters['layout/dimensions'].widthLeftBar - rightSideWidth;
            let usedW = 0;
            for (let name in getters.sectionWidths) {
                if (getters.isVisible(name)) usedW += getters.sectionWidths[name];;
                if (name === sectionName) return usedW < w;
            }
        },

        /**
         * Get items to display in 'required' dropdowns. Optional callback allows for different update 
         * procedures for regular features and checkbox groups, and for filtering out of invalid items, 
         * as the irregular 'occasionally-multiple' select sometimes means that the SelectableList selected
         * items list is incorrect.
         * 
         * @param { Array } currentVals - array of currently selected 'required' item values
         * @param { Function } [updateFn] - called on item select, receives array of filtered selected item values.
         * @returns items to display in the 'required' dropdown SelectableList.
         */
        requiredItems: (state, getters, rootState, rootGetters) => (currentVals, updateFn = () => { }) => {
            let items = [
                {
                    name: 'Never',
                    value: 'never',
                    single: true,
                    callback: () => updateFn(onClick('never')),
                },
                {
                    name: 'Before Submission',
                    value: 'before_submit',
                    single: true,
                    callback: () => updateFn(onClick('before_submit')),
                },
                {
                    name: 'Before any Signature',
                    value: 'before_any_signature',
                    single: true,
                    callback: () => updateFn(onClick('before_any_signature')),
                },
            ];

            for (let feature of rootGetters['form/signatureFeatures']) {
                items.push({
                    name: `Before signature '${feature.name}'`,
                    value: feature.id,
                    disabled: !!rootGetters['form/activeFeatures'].find(af => af.id === feature.id),
                    callback: () => updateFn(onClick(feature.id)),
                });
            }

            return items;

            // toggle clicked item if possible, and deselect any invalid items
            function onClick(clickedVal) {
                let updatedVals = currentVals.slice();
                let singleFields = ['never', 'before_any_signature'];

                if (singleFields.includes(clickedVal)) {
                    // only one value allowed, and can't deselect
                    updatedVals = [clickedVal];
                } else {
                    // toggle clicked item
                    let index = updatedVals.indexOf(clickedVal);
                    if (index !== -1) {
                        updatedVals.splice(index, 1);
                    } else {
                        updatedVals.push(clickedVal);
                    }

                    // remove any selected singleFields
                    updatedVals = updatedVals.filter((v) => !singleFields.includes(v));
                }

                // don't allow empty required array
                if (!updatedVals.length) updatedVals = [clickedVal];

                return updatedVals;
            }
        },
    },
    mutations: {

    },
    actions: {
        async set({ getters, dispatch }, { key, value }) {
            if (key === 'fontSize') {
                if (value === 'Auto') {
                    key = 'autoFontSize';
                    value = true;
                } else if (getters.isSet(getters.val('autoFontSize'))) {
                    await dispatch('form/updateFeatures', { autoFontSize: false }, { root: true });
                }
            }

            await dispatch('form/updateFeatures', { [key]: value }, { root: true });
        },
        async updatePosition({ dispatch }, { key, value }) {
            await dispatch('set', { key, value });
            await dispatch('form/finalizePositionChanges', null, { root: true });
        }
    }
}